import { jsx as _jsx } from "react/jsx-runtime";
import React, { createContext, useContext, useEffect, useMemo, useRef, useState, useCallback } from "react";
import { useChatWebSocket } from "../../hooks/useChatWebSocket";
import { useCall } from "../../hooks/useCall";
import { useChatCrypto } from "../../hooks/useChatCrypto";
import { buildRoomConfigAuthMessage } from "../../utils/roomConfigAuth";
const MobileChatContext = createContext(null);
export function resolveMobileCallAuthorization(securityEnabled, deviceSessionToken, signChatKeyMessage) {
    return {
        deviceSessionToken: securityEnabled ? deviceSessionToken : null,
        signWalletMessage: securityEnabled ? null : signChatKeyMessage,
    };
}
/**
 * Wallet-implementation-neutral chat/call provider.
 *
 * The Dashboard and the separate /m/ client use different wallet context
 * implementations, but must share one signaling, media and E2EE engine.
 * Their thin wrappers provide only the room address and EIP-191 signer.
 */
export const MobileChatProviderCore = ({ children, address, signChatKeyMessage, securityEnabled = false, preloadedV2KeyBytes = null, suppliedDeviceId = null, deviceSessionToken = null, onDeviceSessionRejected = null, }) => {
    const normalizedAddress = useMemo(() => address?.toLowerCase() || "", [address]);
    // In Dashboard security mode no room transport or crypto lifecycle exists
    // until the key, stable identity and device-session proof form one ready
    // scope. Legacy adapters retain their previous eager behavior by default.
    const securityReady = !securityEnabled || (!!normalizedAddress &&
        !!preloadedV2KeyBytes &&
        !!suppliedDeviceId &&
        !!deviceSessionToken);
    const activeAddress = securityReady ? (address || null) : null;
    const [adminPresenceActive, setAdminPresenceActive] = useState(false);
    const chatPageMountsRef = useRef(0);
    const registerChatPagePresence = useCallback(() => {
        chatPageMountsRef.current += 1;
        setAdminPresenceActive(true);
        return () => {
            chatPageMountsRef.current = Math.max(0, chatPageMountsRef.current - 1);
            if (chatPageMountsRef.current === 0)
                setAdminPresenceActive(false);
        };
    }, []);
    const { encryptFn, decryptFn, handleKeySignal, bindSendSignal, keyReady, getV2KeyBytes, retryE2EEKey } = useChatCrypto({
        room: securityReady ? normalizedAddress || null : null,
        signMessage: securityEnabled ? null : signChatKeyMessage,
        enabled: securityReady && !!normalizedAddress,
        preloadedV2KeyBytes: securityEnabled ? preloadedV2KeyBytes : null,
        canProvideRoomKey: securityEnabled ? securityReady : !!signChatKeyMessage,
    });
    const signalHandlerRef = useRef(null);
    const { messages, sendMessage, isConnected, connectedDevices, roomDevices, clearMessages, getMessagesForExport, deviceId, sendCallSignal, archiveEnabled, setArchiveEnabled: setArchiveEnabledRaw, systemNotice, clearSystemNotice, secretMessages, sendSecretMessage, canSecretMessage, isSecretConversationReady, secretConversationRequests, secretMessageError, secretSafetyNumbers, confirmSecretSafetyNumber, } = useChatWebSocket({
        walletAddress: activeAddress,
        encryptFn,
        decryptFn,
        adminPresenceActive,
        // Wallet-socket handshake: proof is cached (~11h), so WalletConnect is
        // prompted at most once per session for moderation rights.
        signWalletAuth: securityEnabled ? null : signChatKeyMessage,
        signChatClear: signChatKeyMessage,
        deviceId: securityEnabled ? suppliedDeviceId || undefined : undefined,
        deviceSessionToken: securityEnabled ? deviceSessionToken : null,
        onDeviceSessionRejected: securityEnabled ? onDeviceSessionRejected : null,
        onCallSignal: (s) => {
            if (handleKeySignal(s))
                return;
            signalHandlerRef.current?.(s);
        },
    });
    bindSendSignal(sendCallSignal);
    const callAuthorization = resolveMobileCallAuthorization(securityEnabled, deviceSessionToken, signChatKeyMessage);
    const { callState, callE2EE, participants, incomingFrom, isMuted, isVideoCall, incomingVideo, isCameraOff, localStream, startCall, acceptCall, rejectCall, hangup, toggleMute, muteAll, unmuteAll, muteAllActive, setParticipantMuted, handRaised, toggleHand, raisedHands, kickParticipant, toggleCamera, switchCamera, switchMicrophone, isScreenSharing, screenShareDisabled, toggleScreenShare, canRejoin, rejoinCall, dismissRejoin, ongoingCall, joinOngoingCall, callDropped, callDroppedByBackground, dismissCallDropped, fileTransfers, shareFile, dismissTransfer, handleSignal, callError, dismissCallError, connectionRecovery, connectionPhase, } = useCall({
        deviceId,
        isConnected,
        sendCallSignal,
        walletAddress: activeAddress,
        deviceSessionToken: callAuthorization.deviceSessionToken,
        signWalletMessage: callAuthorization.signWalletMessage,
        getE2EEKeyMaterial: getV2KeyBytes,
        e2eeKeyAvailable: keyReady,
        // Chat-only-scope guests never receive shared files.
        canReceiveFile: (remoteId) => {
            const dev = roomDevicesRef.current.find((d) => d.deviceId === remoteId);
            if (!dev)
                return true; // unknown (old relay without presence) — allow
            return dev.kind === 'wallet' || dev.scope === 'full';
        },
    });
    const roomDevicesRef = useRef(roomDevices);
    roomDevicesRef.current = roomDevices;
    // File sharing is only offered while a 'full'-scope (dashboard) guest is in
    // the call.
    const canShareFiles = useMemo(() => participants.some((p) => roomDevices.some((d) => d.deviceId === p.deviceId && d.kind === 'guest' && d.scope === 'full')), [participants, roomDevices]);
    useEffect(() => {
        signalHandlerRef.current = handleSignal;
    }, [handleSignal]);
    // Scheduler auto-closed this ad-hoc conference (a scheduled meeting is
    // starting): end any running call locally — the invites are revoked
    // server-side, so the room must be handed over to the meeting.
    useEffect(() => {
        if (systemNotice?.notice === 'conference_closed_for_meeting' && callState !== 'idle') {
            hangup();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [systemNotice]);
    // Owner-only archive toggle: the relay verifies an EIP-191 signature over
    // the exact change, produced with the room wallet key (WalletConnect sign).
    const setArchiveEnabled = React.useCallback(async (enabled) => {
        if (!normalizedAddress || !signChatKeyMessage)
            return;
        const timestamp = Date.now();
        const signature = await signChatKeyMessage(buildRoomConfigAuthMessage(normalizedAddress, enabled, timestamp));
        setArchiveEnabledRaw(enabled, { address: normalizedAddress, timestamp, signature });
    }, [normalizedAddress, signChatKeyMessage, setArchiveEnabledRaw]);
    const value = useMemo(() => ({
        messages,
        sendMessage,
        isConnected,
        connectedDevices,
        clearMessages,
        getMessagesForExport,
        deviceId,
        callState,
        participants,
        incomingFrom,
        isMuted,
        isVideoCall,
        incomingVideo,
        isCameraOff,
        localStream,
        startCall,
        acceptCall,
        rejectCall,
        hangup,
        toggleMute,
        muteAll,
        unmuteAll,
        muteAllActive,
        setParticipantMuted,
        handRaised,
        toggleHand,
        raisedHands,
        kickParticipant,
        toggleCamera,
        switchCamera,
        switchMicrophone,
        isScreenSharing,
        screenShareDisabled,
        toggleScreenShare,
        canRejoin,
        rejoinCall,
        dismissRejoin,
        ongoingCall,
        joinOngoingCall,
        callDropped,
        callDroppedByBackground,
        dismissCallDropped,
        fileTransfers,
        shareFile,
        dismissTransfer,
        canShareFiles,
        callError,
        dismissCallError,
        connectionRecovery,
        connectionPhase,
        callE2EE,
        keyReady,
        retryE2EEKey,
        archiveEnabled,
        setArchiveEnabled,
        systemNotice,
        clearSystemNotice,
        encryptFn,
        decryptFn,
        registerChatPagePresence,
        secretMessages,
        sendSecretMessage,
        canSecretMessage,
        isSecretConversationReady,
        secretConversationRequests,
        secretMessageError,
        secretSafetyNumbers,
        confirmSecretSafetyNumber,
    }), [
        messages,
        sendMessage,
        isConnected,
        connectedDevices,
        clearMessages,
        getMessagesForExport,
        deviceId,
        secretMessages,
        sendSecretMessage,
        canSecretMessage,
        isSecretConversationReady,
        secretConversationRequests,
        secretMessageError,
        secretSafetyNumbers,
        confirmSecretSafetyNumber,
        callState,
        participants,
        incomingFrom,
        isMuted,
        isVideoCall,
        incomingVideo,
        isCameraOff,
        localStream,
        startCall,
        acceptCall,
        rejectCall,
        hangup,
        toggleMute,
        muteAll,
        unmuteAll,
        muteAllActive,
        setParticipantMuted,
        handRaised,
        toggleHand,
        raisedHands,
        kickParticipant,
        toggleCamera,
        switchCamera,
        switchMicrophone,
        isScreenSharing,
        screenShareDisabled,
        toggleScreenShare,
        canRejoin,
        rejoinCall,
        ongoingCall,
        joinOngoingCall,
        callDropped,
        callDroppedByBackground,
        dismissCallDropped,
        fileTransfers,
        shareFile,
        dismissTransfer,
        canShareFiles,
        callError,
        dismissCallError,
        connectionRecovery,
        callE2EE,
        keyReady,
        retryE2EEKey,
        archiveEnabled,
        setArchiveEnabled,
        systemNotice,
        clearSystemNotice,
        encryptFn,
        decryptFn,
        registerChatPagePresence,
    ]);
    return _jsx(MobileChatContext.Provider, { value: value, children: children });
};
export function useMobileChat() {
    const ctx = useContext(MobileChatContext);
    if (!ctx) {
        throw new Error("useMobileChat must be used within a MobileChatProvider");
    }
    return ctx;
}
